/**
 * Country catalogue and per-country vocabulary.
 *
 * Global from day one: US, Canada, UK, Australia, New Zealand. Each country brings its own
 * words for its administrative levels, so the UI never says "county" in the UK or "state" in
 * Canada. Getting this wrong is the kind of thing a local-government product cannot afford —
 * the vocabulary is the credibility.
 *
 * This is the same catalogue Search's scope chooser uses ("Pick places"), deliberately: the
 * two surfaces must speak identically about places, so they share one source.
 */
import type { CountryCode } from '@/lib/tier';

export interface CountryMeta {
  code: CountryCode;
  name: string;
  /** The county-equivalent level: what groups towns together. */
  unit: string;
  /** The state-equivalent level: the top administrative division. */
  region: string;
  /**
   * Whether school boards are separately elected bodies here. Only the US and Canada run
   * schools through their own elected boards; elsewhere the council covers them, so a
   * Board of Ed badge would be meaningless.
   */
  electedSchoolBoards: boolean;
}

export const COUNTRIES: readonly CountryMeta[] = [
  { code: 'US', name: 'United States', unit: 'County', region: 'State', electedSchoolBoards: true },
  { code: 'CA', name: 'Canada', unit: 'Region', region: 'Province', electedSchoolBoards: true },
  {
    code: 'GB',
    name: 'United Kingdom',
    unit: 'Council area',
    region: 'Nation',
    electedSchoolBoards: false,
  },
  { code: 'AU', name: 'Australia', unit: 'Region', region: 'State', electedSchoolBoards: false },
  {
    code: 'NZ',
    name: 'New Zealand',
    unit: 'District',
    region: 'Region',
    electedSchoolBoards: false,
  },
];

export function countryMeta(code: CountryCode): CountryMeta {
  const found = COUNTRIES.find((c) => c.code === code);
  if (!found) throw new Error(`Unknown country: ${code}`);
  return found;
}

/** "All states" / "All provinces" / "All nations" — lower-cased plural of the region word. */
export function allRegionsLabel(code: CountryCode): string {
  return `All ${countryMeta(code).region.toLowerCase()}s`;
}

/** One county-equivalent grouping of towns. */
export interface CatalogUnit {
  country: CountryCode;
  /** Region code, e.g. "NJ", "ON", "ENG". */
  regionCode: string;
  /** Full region name, e.g. "New Jersey", "Ontario", "England". */
  regionName: string;
  /** The unit's own name — rendered with its country's vocabulary. */
  unitName: string;
  towns: string[];
}

/**
 * How a unit is named in running text. US counties read "Bergen County"; a UK council area or
 * a Canadian region is named on its own.
 */
export function unitDisplayName(unit: CatalogUnit): string {
  return unit.country === 'US' ? `${unit.unitName} County` : unit.unitName;
}
